import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MapPin, Bell, MessageSquare, ClipboardList, LayoutGrid, User, Globe, LogOut, Settings, HelpCircle, Shield, Sparkles } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { LANGUAGES } from "@/i18n";
import API from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, t } = useApp();
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);
  const [zoneLabel, setZoneLabel] = useState("");

  useEffect(() => { document.title = "FYDATE"; }, []);

  useEffect(() => {
    if (!user) { setUnread(0); setZoneLabel(""); return; }
    const fetchUnread = () => API.get("/notifications/unread-count").then((r) => setUnread(r.data.count || 0)).catch(() => {});
    fetchUnread();
    API.get("/me/full").then((r) => {
      const z = r.data.zone;
      if (z?.start_point?.city) {
        const rad = z.general_radius === "all" ? "tutto il paese" : `${z.general_radius} km`;
        setZoneLabel(`${z.start_point.city} · ${rad}`);
      }
    }).catch(() => {});
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, [user, location.pathname]);

  const NavItem = ({ path, icon: Icon, label, testid }) => {
    const active = location.pathname === path;
    return (
      <button data-testid={testid} onClick={() => navigate(path)}
        className={`flex flex-col items-center gap-0.5 flex-1 py-1 text-[11px] font-bold transition-colors ${active ? "text-[#1F4FE0]" : "text-[#0F1230] hover:text-[#1F4FE0]"}`}>
        <Icon size={24} strokeWidth={active ? 2.2 : 1.8} />
        {label}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#EEF1F7] flex flex-col">
      <header className="sticky top-0 z-40 bg-[#1F4FE0] rounded-b-[26px] shadow-[0_6px_20px_-10px_rgba(31,79,224,0.6)]">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-2">
          <button data-testid="header-logo" onClick={() => navigate("/")} className="flex items-center gap-2">
            <img src="/fydate-icon.png" alt="FYDATE" className="w-9 h-9 rounded-lg object-cover" />
            <span className="text-xl font-extrabold text-white tracking-tight">FYDATE</span>
          </button>

          {user && zoneLabel && (
            <button data-testid="zone-picker-button" onClick={() => navigate("/zone")}
              className="ml-auto inline-flex items-center gap-1 rounded-full bg-white px-3 h-9 text-sm font-semibold text-[#0F1230] max-w-[42%] truncate hover:shadow-md transition-shadow">
              <MapPin size={15} className="text-[#1F4FE0] shrink-0" /> <span className="truncate">{zoneLabel}</span>
            </button>
          )}

          {user ? (
            <>
              <button data-testid="notifications-button" onClick={() => navigate("/notifications")} className={`relative p-2 text-white ${zoneLabel ? "" : "ml-auto"}`}>
                <Bell size={22} />
                {unread > 0 && <span data-testid="nav-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FFC400] text-[#0F1230] text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-button" className="rounded-full w-9 h-9 overflow-hidden flex items-center justify-center ring-2 ring-white/70">
                    <Avatar src={user?.picture} name={user?.name} size={36} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profile")}><User size={16} className="mr-2" /> {t("profile")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-availabilities" onClick={() => navigate("/my-availabilities")}><ClipboardList size={16} className="mr-2" /> {t("myAvailabilities")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-messages" onClick={() => navigate("/messages")}><MessageSquare size={16} className="mr-2" /> {t("messages")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-zone" onClick={() => navigate("/zone")}><MapPin size={16} className="mr-2" /> {t("zone")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")}><Settings size={16} className="mr-2" /> {t("settings")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-how" onClick={() => navigate("/how-it-works")}><HelpCircle size={16} className="mr-2" /> {t("howItWorks")}</DropdownMenuItem>
                  {user?.role === "admin" && <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}><Shield size={16} className="mr-2" /> Admin</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1">
                    <div className="flex items-center gap-1 text-xs text-[#4A4E68] mb-1"><Globe size={13} /> Lingua</div>
                    <div className="flex flex-wrap gap-1">
                      {LANGUAGES.map((l) => (
                        <button key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLanguage(l.code)} className={`text-xs px-2 py-1 rounded-full border ${language === l.code ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA]"}`}>{l.code.toUpperCase()}</button>
                      ))}
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={logout}><LogOut size={16} className="mr-2" /> {t("logout")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <button data-testid="header-login" onClick={() => navigate("/login")} className="ml-auto bg-[#FFC400] text-[#0F1230] rounded-full px-5 h-9 text-sm font-bold hover:scale-105 active:scale-95 transition-transform duration-150">Accedi</button>
          )}
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-5 pb-28">{children}</main>

      <footer className="max-w-4xl mx-auto w-full px-4 pb-28 md:pb-6 text-center">
        <button data-testid="footer-promote" onClick={() => navigate("/per-attivita")} className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-[#1F4FE0] rounded-full px-5 h-11 mb-3 hover:-translate-y-0.5 transition-transform duration-150">
          <Sparkles size={15} className="text-[#FFC400]" /> Hai un'attività? <span className="text-[#FFC400]">Fatti trovare</span>
        </button>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-[#4A4E68] mb-2">
          <button data-testid="footer-about" onClick={() => navigate("/chi-siamo")} className="hover:text-[#1F4FE0] underline-offset-2 hover:underline">Chi siamo</button>
          <button data-testid="footer-how" onClick={() => navigate("/come-funziona")} className="hover:text-[#1F4FE0] underline-offset-2 hover:underline">Come funziona</button>
          <button data-testid="footer-contacts" onClick={() => navigate("/contatti")} className="hover:text-[#1F4FE0] underline-offset-2 hover:underline">Contatti</button>
          <button data-testid="footer-business-link" onClick={() => navigate("/per-attivita")} className="hover:text-[#1F4FE0] underline-offset-2 hover:underline">Per le attività</button>
          <button data-testid="footer-promote-link" onClick={() => navigate("/promuovi")} className="hover:text-[#1F4FE0] underline-offset-2 hover:underline">Promuovi la tua attività</button>
          <button data-testid="footer-privacy" onClick={() => navigate("/privacy")} className="hover:text-[#1F4FE0] underline-offset-2 hover:underline">Privacy</button>
          <button data-testid="footer-cookie" onClick={() => navigate("/cookie")} className="hover:text-[#1F4FE0] underline-offset-2 hover:underline">Cookie</button>
          <button data-testid="footer-terms" onClick={() => navigate("/terms")} className="hover:text-[#1F4FE0] underline-offset-2 hover:underline">Termini</button>
        </div>
        <p data-testid="footer-branding" className="text-sm text-[#4A4E68] inline-flex items-center justify-center gap-1.5">
          <img src="/fydate-icon.png" alt="" className="w-5 h-5 rounded object-cover" />
          <span><span className="font-extrabold text-[#0F1230]">FYDATE</span> — La bacheca dei bisogni</span>
        </p>
      </footer>

      <nav className="fixed bottom-0 left-0 right-0 z-50 flex justify-center md:hidden pointer-events-none">
        <div className="pointer-events-auto w-full max-w-md bg-white rounded-t-[26px] shadow-[0_-6px_24px_-10px_rgba(15,18,48,0.25)] h-[68px] flex items-center">
          <NavItem path="/" icon={LayoutGrid} label={t("activity")} testid="bottomnav-activity" />
          <NavItem path="/my-requests" icon={ClipboardList} label={t("myRequests")} testid="bottomnav-my-requests" />
        </div>
      </nav>
    </div>
  );
}
